import React from 'react'
import { Group, Input, Select, Text, TextInput, Tooltip } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form/lib/types'
import { CheckIcon, Cross2Icon, CrossCircledIcon } from '@radix-ui/react-icons'

import { PresenceButton, RemoveAttendeeButton } from './AttendeeControls'
import { useAttendeeField } from './useAttendeeField'

import { AttendeeRole } from '@/constants'
import { AttendeeFormValues, ReporterFeedback } from '@/types'

export const DrAttendeeInput: React.FC<{
  roles: AttendeeRole[]
  onBlur?: () => void
  form: UseFormReturnType<ReporterFeedback>
  witnessIndex: number
  index: number
  attendeeWarnings: Set<keyof AttendeeFormValues>[]
}> = ({ roles, onBlur, form, index, attendeeWarnings, witnessIndex }) => {
  const {
    values,
    valuePrefix,
    updateValueAndPropagateForward,
    roleData,
    formWarnings,
  } = useAttendeeField({ form, witnessIndex, index, roles, attendeeWarnings })

  return (
    <div className="flex space-x-4 items-start">
      <TextInput
        required
        disabled={values?.present === false}
        label="Name"
        {...form.getInputProps(`${valuePrefix}.name`)}
        onChange={event =>
          updateValueAndPropagateForward('name', event.target.value)
        }
        className="flex-1"
        {...formWarnings.getInputWarningProps(index, 'name')}
      />
      <TextInput
        disabled={values?.present === false}
        label="Email"
        type="email"
        {...form.getInputProps(`${valuePrefix}.email`)}
        value={values?.email ?? ''}
        onChange={event =>
          updateValueAndPropagateForward('email', event.target.value || null)
        }
        className="flex-1"
        {...formWarnings.getInputWarningProps(index, `email`)}
      />
      <Select
        required
        styles={{ item: { wordBreak: 'normal' } }}
        disabled={values?.present === false}
        label="Role"
        {...form.getInputProps(`${valuePrefix}.role`)}
        data={roleData}
        onChange={value => updateValueAndPropagateForward('role', value)}
        {...formWarnings.getInputWarningProps(index, `role`)}
      />
      <Input.Wrapper label="Present" required>
        <Group mt={10}>
          <Tooltip label="Absent" withArrow position="top">
            <PresenceButton
              color="red"
              error={Boolean(form.errors[`${valuePrefix}.present`])}
              active={values?.present === false}
              onClick={() => {
                form.setFieldValue(`${valuePrefix}.present`, false)
              }}
              onBlur={onBlur}
            >
              <Cross2Icon />
            </PresenceButton>
          </Tooltip>
          <Tooltip label="Present" withArrow position="top">
            <PresenceButton
              color="green"
              error={Boolean(form.errors[`${valuePrefix}.present`])}
              active={values?.present === true}
              onClick={() => form.setFieldValue(`${valuePrefix}.present`, true)}
              onBlur={onBlur}
            >
              <CheckIcon />
            </PresenceButton>
          </Tooltip>
          <Tooltip label="Remove attendee" withArrow position="top">
            <RemoveAttendeeButton
              onClick={() =>
                form.removeListItem(
                  `events_feedbacks.${witnessIndex}.attendees`,
                  index,
                )
              }
              onBlur={onBlur}
            >
              <CrossCircledIcon />
            </RemoveAttendeeButton>
          </Tooltip>
        </Group>
        {form.errors[`${valuePrefix}.present`] && (
          <Text color="red" size="sm" className="mt-2 w-16">
            {form.errors[`${valuePrefix}.present`]}
          </Text>
        )}
      </Input.Wrapper>
    </div>
  )
}
